// ─── Escala semántica de estado — fuente única de verdad ───────────────────────
// Antes: Dashboard.js, WorkOrders.js y MaintenanceOrders.js pintaban el MISMO enum
// de estado de OT (PENDING/ASSIGNED/IN_PROGRESS/COMPLETED/CANCELED) con tres paletas
// de color distintas, y Projects.js reutilizaba por casualidad los mismos hex de
// WorkOrders.js pero con significado semántico distinto (hallazgo de auditoría #03).
//
// Ahora hay un solo lugar con 5 tonos (neutral/info/warning/success/danger), y cada
// dominio (OT, orden de mantenimiento, proyecto) mapea sus propios valores de estado
// a uno de esos tonos. Un gris siempre es el mismo gris, un verde siempre el mismo
// verde, en toda la app.

// ─── Tonos base ─────────────────────────────────────────────────────────────
// Paleta tomada de MaintenanceOrders.js por ser la más sistemática de las tres
// (estilo Tailwind, buen contraste texto/fondo).
export const STATUS_TONES = {
  neutral: { backgroundColor: '#f3f4f6', color: '#6b7280', border: '1px solid #d1d5db' },
  info:    { backgroundColor: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' },
  warning: { backgroundColor: '#fff7ed', color: '#ea580c', border: '1px solid #fed7aa' },
  success: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' },
  danger:  { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' },
  // Tono distinto de 'danger' (reservado para CANCELED): señala una excepción que
  // requiere revisión del administrador sin decir "cancelada" ni "completada".
  // Usado hoy solo por MaintenanceOrders.js cuando el técnico cierra el reporte
  // con el checklist incompleto (ver hallazgo de auditoría de mantenimiento).
  alert:   { backgroundColor: '#fdf2f8', color: '#be185d', border: '1px solid #fbcfe8' },
};

// ─── Mapeo por dominio ───────────────────────────────────────────────────────

// Estado de Orden de Trabajo / Orden de Mantenimiento — mismo enum de backend
// (OrderStatus: PENDING | ASSIGNED | IN_PROGRESS | COMPLETED | CANCELED).
// Usado en Dashboard.js, WorkOrders.js y MaintenanceOrders.js: los tres deben
// importar este mismo mapeo, nunca redefinirlo.
export const ORDER_STATUS_TONE = {
  PENDING: 'neutral',
  ASSIGNED: 'info',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  CANCELED: 'danger',
};

// Estado de Proyecto — dominio distinto (Project.status: active | inactive | completed),
// pero reutiliza la MISMA escala STATUS_TONES para que un color nunca cambie de
// significado de una página a otra:
// - active: proyecto en curso y saludable → success (verde)
// - completed: ciclo finalizado, informativo, no requiere acción → info (azul)
// - inactive: pausado / sin actividad → neutral (gris)
export const PROJECT_STATUS_TONE = {
  active: 'success',
  inactive: 'neutral',
  completed: 'info',
};

/**
 * @description Genera el estilo inline de un badge de estado a partir de un tono semántico.
 * Centraliza el padding/radius/fontSize para que todos los badges de estado
 * de la app luzcan igual, sin importar el dominio.
 * @param {string} tone Uno de los tonos de STATUS_TONES ('neutral'|'info'|'warning'|'success'|'danger').
 * @returns {object} Objeto de estilos inline listo para usar en `style={}`.
 */
export const getStatusBadgeStyle = (tone) => ({
  padding: '2px 8px',
  borderRadius: '12px',
  fontSize: '0.8rem',
  fontWeight: 'bold',
  // Sin esto, una etiqueta larga (ej. "No Completada") se envuelve en dos líneas
  // dentro del pill y se ve cortada — la columna debe crecer o el contenedor
  // scrollear (.table-container ya tiene overflow-x: auto), nunca partir el texto.
  whiteSpace: 'nowrap',
  ...(STATUS_TONES[tone] || STATUS_TONES.neutral),
});

export default STATUS_TONES;
